import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { FadeUp } from "../components/Reveal";
import { SERVICES } from "../data/services";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { CalendarCheck } from "@phosphor-icons/react";

const inputCls = "h-12 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

export default function Demo() {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", service: "", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/demo-requests", { ...form, ref });
      toast.success("Demo request received — we'll confirm your slot within 24 hours.");
      setForm({ name: "", email: "", organization: "", role: "", service: "", preferred_date: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        testId="demo-page"
        overline="Talk to Us"
        title={["See the platform", "in action."]}
        subtitle="A 45-minute working session with our solutions team — tailored to your service area, your systems, and your questions."
      />
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp>
            <form onSubmit={submit} data-testid="demo-form" className="rounded-3xl border border-primary/10 bg-white p-8 lg:p-12 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="demo-name">Full name *</label>
                  <Input id="demo-name" data-testid="demo-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} placeholder="Dr. Asha Verma" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="demo-email">Work email *</label>
                  <Input id="demo-email" data-testid="demo-email-input" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="asha@hospital.org" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="demo-org">Organization *</label>
                  <Input id="demo-org" data-testid="demo-org-input" required value={form.organization} onChange={set("organization")} className={`mt-2 ${inputCls}`} placeholder="City General Hospital" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="demo-role">Your role</label>
                  <Input id="demo-role" data-testid="demo-role-input" value={form.role} onChange={set("role")} className={`mt-2 ${inputCls}`} placeholder="Chief Medical Information Officer" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="demo-service">Service area</label>
                  <select id="demo-service" data-testid="demo-service-select" value={form.service} onChange={set("service")} className={`mt-2 w-full rounded-md border px-3 text-sm ${inputCls}`}>
                    <option value="">Select a service area</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="demo-date">Preferred date</label>
                  <Input id="demo-date" data-testid="demo-date-input" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={`mt-2 ${inputCls}`} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="demo-message">What would you like to see?</label>
                <Textarea id="demo-message" data-testid="demo-message-input" rows={4} value={form.message} onChange={set("message")} className="mt-2 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" placeholder="Current systems, specific workflows, questions for our team…" />
              </div>
              <Button
                type="submit"
                data-testid="demo-submit-btn"
                disabled={submitting}
                className="rounded-full bg-primary px-8 h-12 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
              >
                <CalendarCheck size={18} className="mr-2" />
                {submitting ? "Requesting…" : "Request Demo"}
              </Button>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
